import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLanguage } from "@/context/LanguageContext";
import { dealerPortalLabels } from "@/i18n";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDealerPortal } from "@/hooks/useDealerPortal";
import { dealerPortalService } from "@/services/dealerPortal/dealerPortalService";
import { useMedia } from "@/hooks/useMedia";
import { useFile } from "@/hooks/useFile";
import { toast } from "sonner";
import type {
  DealerPortalCustomer,
  DealerPortalCustomerNote,
  DealerPortalCustomerHistoryEvent,
  DealerPortalCustomerVehicleConnection,
  DealerPortalCustomerDocument,
  DealerPortalCustomerType,
  DealerPortalVehicle,
} from "@/types/dealerPortal";
import {
  Pencil,
  FileText,
  Clock,
  Download,
  Upload,
  Trash2,
  Loader2,
} from "lucide-react";
import { FileUploadTrigger } from "@/components/native/FileUploadTrigger";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  formatPersonalNumberYYMMDD,
  normalizePersonalNumberYYMMDD,
  validatePersonalNumberYYMMDD,
  validateEmailOptional,
  validatePhoneDigitsOnlyMin7,
  validatePostalCodeDigitsOnly,
  validateCityOptional,
  validateCountryOptional,
  validateNameDealerPortal,
} from "@/utils/formValidation";
import { getLanguage } from "@/utils/languageUtils";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useDealerPortalNativeSheetProps } from "@/lib/native/useDealerPortalNativeSheetProps";

const PHONE_DIGITS = /^\d*$/;
const CUSTOMER_TYPE_BADGE_CLASS: Record<DealerPortalCustomerType, string> = {
  PRIVATE:
    "text-[10px] font-normal bg-slate-100 text-slate-800 dark:bg-slate-700/60 dark:text-slate-200",
  DEALER:
    "text-[10px] font-normal bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200",
  BUSINESS:
    "text-[10px] font-normal bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-200",
};
const NAME_LETTERS_SPACES = /^[A-ZÅÄÖa-zåäö\s]*$/;
const CITY_COUNTRY_LETTERS = /^[A-ZÅÄÖa-zåäö\s]*$/;
const POSTAL_DIGITS = /^\d*$/;

interface Props {
  customerId: string | null;
  open: boolean;
  onClose: () => void;
  onUpdated?: () => void;
}

export function DealerPortalCustomerCardDialog({
  customerId,
  open,
  onClose,
  onUpdated,
}: Props) {
  const { language } = useLanguage();
  const d = dealerPortalLabels(language ?? "sv");
  const c = d.customers as Record<string, string>;

  const {
    customer,
    updateCustomer,
    updateCustomerVehicleConnections,
    deleteCustomer,
    addCustomerNote,
    uploadCustomerDocument,
    deleteCustomerDocument,
  } = useDealerPortal({
    customerId: open ? customerId : null,
  });
  const { getDownloadUrlAsync } = useMedia();
  const { downloadFile } = useFile();
  const nativeSheetProps = useDealerPortalNativeSheetProps(open, "max-w-4xl p-4");

  const [editing, setEditing] = useState(false);
  const [linkedVehicles, setLinkedVehicles] = useState<
    { id: string; regNr: string }[]
  >([]);

  const vehiclesQuery = useQuery({
    queryKey: ["dealer-portal", "vehicles-for-customer"],
    queryFn: () => dealerPortalService.getVehicles(),
    enabled: editing && open,
  });
  const companyVehicles = (vehiclesQuery.data ?? []) as DealerPortalVehicle[];

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [personalNumber, setPersonalNumber] = useState("");
  const [street, setStreet] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [companyConnection, setCompanyConnection] = useState("");
  const [orgNumber, setOrgNumber] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [contactPersonName, setContactPersonName] = useState("");
  const [contactPersonEmail, setContactPersonEmail] = useState("");
  const [contactPersonPhone, setContactPersonPhone] = useState("");
  const [customerType, setCustomerType] =
    useState<DealerPortalCustomerType>("PRIVATE");
  const [isBuyer, setIsBuyer] = useState(false);
  const [isSeller, setIsSeller] = useState(false);
  const [noteContent, setNoteContent] = useState("");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const cust = customer.data;
  const isLoading = customer.isLoading;

  // Stäng redigeringsläge när dialog stängs eller när man byter kund
  useEffect(() => {
    if (!open) {
      setEditing(false);
      setLinkedVehicles([]);
    }
  }, [open]);
  useEffect(() => {
    setEditing(false);
    setLinkedVehicles([]);
  }, [customerId]);

  const startEditing = () => {
    if (cust) {
      setName(cust.name ?? "");
      setEmail(cust.email ?? "");
      setPhone(cust.phone ?? "");
      setPersonalNumber(
        normalizePersonalNumberYYMMDD(cust.personalNumber ?? ""),
      );
      setStreet(cust.street ?? "");
      setPostalCode(cust.postalCode ?? "");
      setCity(cust.city ?? "");
      setCountry(cust.country ?? "");
      setCompanyConnection(cust.companyConnection ?? "");
      setOrgNumber(cust.orgNumber ?? "");
      setVatNumber(cust.vatNumber ?? "");
      setContactPersonName(cust.contactPersonName ?? "");
      setContactPersonEmail(cust.contactPersonEmail ?? "");
      setContactPersonPhone(cust.contactPersonPhone ?? "");
      setCustomerType(
        (cust.customerType ?? "PRIVATE") as DealerPortalCustomerType,
      );
      setIsBuyer(cust.isBuyer ?? false);
      setIsSeller(cust.isSeller ?? false);
      setLinkedVehicles(
        (cust.vehicleConnections ?? []).map(
          (vc: DealerPortalCustomerVehicleConnection) => ({
            id: vc.vehicleId,
            regNr: vc.vehicle?.regNr ?? vc.vehicleId,
          }),
        ),
      );
      setEditing(true);
    }
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!cust) return;
    const lang = language ?? getLanguage();
    const isCompanyType =
      customerType === "BUSINESS" || customerType === "DEALER";
    const nameRes = validateNameDealerPortal(name, undefined, lang);
    if (!nameRes.isValid) {
      toast.error(nameRes.errors[0]);
      return;
    }
    if (!isCompanyType) {
      const pnRes = validatePersonalNumberYYMMDD(
        personalNumber,
        undefined,
        lang,
      );
      if (!pnRes.isValid) {
        toast.error(pnRes.errors[0]);
        return;
      }
    }
    const emailToValidate = isCompanyType ? contactPersonEmail : email;
    const phoneToValidate = phone;
    if (emailToValidate.trim()) {
      const emailRes = validateEmailOptional(emailToValidate, undefined, lang);
      if (!emailRes.isValid) {
        toast.error(emailRes.errors[0]);
        return;
      }
    }
    if (phoneToValidate.trim()) {
      const phoneRes = validatePhoneDigitsOnlyMin7(
        phoneToValidate,
        undefined,
        lang,
      );
      if (!phoneRes.isValid) {
        toast.error(phoneRes.errors[0]);
        return;
      }
    }
    const postalRes = validatePostalCodeDigitsOnly(postalCode, undefined, lang);
    if (!postalRes.isValid) {
      toast.error(postalRes.errors[0]);
      return;
    }
    const cityRes = validateCityOptional(city, undefined, lang);
    if (!cityRes.isValid) {
      toast.error(cityRes.errors[0]);
      return;
    }
    const countryRes = validateCountryOptional(country, undefined, lang);
    if (!countryRes.isValid) {
      toast.error(countryRes.errors[0]);
      return;
    }
    updateCustomer.mutate(
      {
        id: cust.id,
        body: {
          name: name.trim() || undefined,
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          personalNumber: isCompanyType
            ? undefined
            : personalNumber.trim() || undefined,
          street: street.trim() || undefined,
          postalCode: postalCode.trim() || undefined,
          city: city.trim() || undefined,
          country: country.trim() || undefined,
          companyConnection: companyConnection.trim() || undefined,
          orgNumber: orgNumber.trim() || undefined,
          vatNumber: vatNumber.trim() || undefined,
          contactPersonName: contactPersonName.trim() || undefined,
          contactPersonEmail: contactPersonEmail.trim() || undefined,
          contactPersonPhone: contactPersonPhone.trim() || undefined,
          isBuyer,
          isSeller,
        },
      },
      {
        onSuccess: () => {
          updateCustomerVehicleConnections.mutate(
            {
              customerId: cust.id,
              vehicleIds: linkedVehicles.map((v) => v.id),
            },
            {
              onSuccess: () => {
                setEditing(false);
                onUpdated?.();
                toast.success(c.toastUpdated ?? "Kund uppdaterad");
              },
              onError: (err: unknown) => {
                toast.error(
                  (err as { message?: string })?.message ??
                    c.toastUpdateError ??
                    "Kunde inte uppdatera",
                );
              },
            },
          );
        },
        onError: (err: unknown) => {
          toast.error(
            (err as { message?: string })?.message ??
              c.toastUpdateError ??
              "Kunde inte uppdatera",
          );
        },
      },
    );
  };

  const handleAddNote = () => {
    if (!cust || !noteContent.trim()) return;
    addCustomerNote.mutate(
      { customerId: cust.id, content: noteContent.trim() },
      {
        onSuccess: () => {
          setNoteContent("");
          toast.success(c.toastNoteAdded ?? "Anteckning tillagd");
        },
        onError: () =>
          toast.error(
            c.toastNoteAddError ?? "Kunde inte lägga till anteckning",
          ),
      },
    );
  };

  const displayName = cust?.name?.trim() || cust?.email || "—";

  const handleUploadDocument = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !cust) return;
    e.target.value = "";
    setUploadingDoc(true);
    uploadCustomerDocument.mutate(
      { customerId: cust.id, file },
      {
        onSuccess: () => {
          onUpdated?.();
          toast.success(c.documentUploaded ?? "Dokument uppladdat");
        },
        onError: (err: unknown) => {
          toast.error(
            (err as { message?: string })?.message ??
              c.documentUploadError ??
              "Kunde inte ladda upp dokument",
          );
        },
        onSettled: () => setUploadingDoc(false),
      },
    );
  };

  const handleDownloadDocument = async (doc: DealerPortalCustomerDocument) => {
    const mediaId = doc.media?.id;
    if (!mediaId) return;
    try {
      const url = await getDownloadUrlAsync({ mediaId, expiresIn: 3600 });
      await downloadFile(url, doc.media?.originalName ?? "document");
      toast.success(c.downloadStarted ?? "Nedladdning startad");
    } catch {
      toast.error(c.downloadFailed ?? "Kunde inte ladda ned");
    }
  };

  const handleDeleteDocument = (doc: DealerPortalCustomerDocument) => {
    const mediaId = doc.media?.id;
    if (!mediaId || !cust) return;
    setDeletingDocId(doc.id);
    deleteCustomerDocument.mutate(
      { customerId: cust.id, mediaId },
      {
        onSuccess: () => {
          onUpdated?.();
          toast.success(c.documentRemoved ?? "Dokument borttaget");
        },
        onError: () =>
          toast.error(c.documentRemoveError ?? "Kunde inte ta bort dokument"),
        onSettled: () => setDeletingDocId(null),
      },
    );
  };

  const handleDeleteCustomer = () => {
    if (!cust) return;
    deleteCustomer.mutate(cust.id, {
      onSuccess: () => {
        onUpdated?.();
        onClose();
        toast.success(c.toastDeleted ?? "Kund borttagen");
      },
      onError: (err: unknown) => {
        toast.error(
          (err as { message?: string })?.message ??
            c.toastDeleteError ??
            "Kunde inte radera kund",
        );
      },
    });
    setShowDeleteConfirm(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent {...nativeSheetProps}>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-lg font-semibold">
              {isLoading ? "..." : displayName}
            </h2>
          </div>

          {cust && (
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="w-full flex sm:grid sm:grid-cols-4 justify-start overflow-x-auto overflow-y-hidden flex-nowrap scrollbar-hide h-9 sm:h-10 p-1 text-xs sm:text-sm font-normal gap-0.5 sm:gap-0">
                <TabsTrigger
                  value="info"
                  className="flex-none shrink-0 sm:flex-1 text-xs sm:text-sm font-normal px-2 sm:px-3 py-1.5 whitespace-nowrap"
                >
                  {c.tabInfo ?? "Namn & Koppling"}
                </TabsTrigger>
                <TabsTrigger
                  value="documents"
                  className="flex-none shrink-0 sm:flex-1 text-xs sm:text-sm font-normal px-2 sm:px-3 py-1.5 whitespace-nowrap"
                >
                  {c.tabDocuments ?? "Dokument"}
                </TabsTrigger>
                <TabsTrigger
                  value="history"
                  className="flex-none shrink-0 sm:flex-1 text-xs sm:text-sm font-normal px-2 sm:px-3 py-1.5 whitespace-nowrap"
                >
                  {c.tabHistory ?? "Historik"}
                </TabsTrigger>
                <TabsTrigger
                  value="notes"
                  className="flex-none shrink-0 sm:flex-1 text-xs sm:text-sm font-normal px-2 sm:px-3 py-1.5 whitespace-nowrap"
                >
                  {c.tabNotes ?? "Anteckningar"}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="mt-3 space-y-4">
                {editing ? (
                  <form onSubmit={handleSave} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-muted-foreground text-xs">
                          {c.nameRequired ?? "Namn"} *
                        </Label>
                        <Input
                          value={name}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (NAME_LETTERS_SPACES.test(v) || v === "")
                              setName(v);
                          }}
                          required
                        />
                        <Label className="text-muted-foreground">
                          {c.contactInfo ?? "Kontaktinformation"}
                        </Label>
                        {customerType === "BUSINESS" ||
                        customerType === "DEALER" ? (
                          <>
                            <div className="space-y-2">
                              <Label className="text-muted-foreground text-xs">
                                {c.orgNr ?? "Org.nr"}
                              </Label>
                              <Input
                                value={orgNumber}
                                onChange={(e) => setOrgNumber(e.target.value)}
                                placeholder={
                                  c.placeholderOrgNumber ?? "556677-8899"
                                }
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-muted-foreground text-xs">
                                {customerType === "BUSINESS" ||
                                customerType === "DEALER"
                                  ? (c.phoneCompany ??
                                    c.phone ??
                                    "Telefon (företaget)")
                                  : (c.phone ?? "Telefon")}
                              </Label>
                              <Input
                                value={phone}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v === "" || PHONE_DIGITS.test(v))
                                    setPhone(v);
                                }}
                                placeholder={
                                  c.placeholderPhone ?? "070-123 45 67"
                                }
                              />
                            </div>
                            <Label className="text-muted-foreground text-xs block pt-1">
                              {c.contactPerson ?? "Kontaktperson"}
                            </Label>
                            <Input
                              value={contactPersonName}
                              onChange={(e) =>
                                setContactPersonName(e.target.value)
                              }
                              placeholder={c.placeholderName ?? "Namn"}
                            />
                          </>
                        ) : (
                          <>
                            <div className="space-y-2">
                              <Label className="text-muted-foreground text-xs">
                                {c.personalNumber ?? "Personnummer"}
                              </Label>
                              <Input
                                value={personalNumber}
                                onChange={(e) =>
                                  setPersonalNumber(
                                    formatPersonalNumberYYMMDD(e.target.value),
                                  )
                                }
                                placeholder={
                                  c.placeholderPersonalNumber ?? "YYMMDD-XXXX"
                                }
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-muted-foreground text-xs">
                                {c.email ?? "E-post"}
                              </Label>
                              <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-muted-foreground text-xs">
                                {c.phone ?? "Telefon"}
                              </Label>
                              <Input
                                value={phone}
                                onChange={(e) =>
                                  PHONE_DIGITS.test(e.target.value) &&
                                  setPhone(e.target.value)
                                }
                              />
                            </div>
                          </>
                        )}
                        <div className="space-y-2">
                          <Label className="text-muted-foreground text-xs">
                            {c.role ?? "Roll"}
                          </Label>
                          <div className="flex gap-6">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <Checkbox
                                checked={isBuyer}
                                onCheckedChange={(v) => setIsBuyer(!!v)}
                              />
                              <span className="text-sm">
                                {c.buyer ?? "Köpare"}
                              </span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <Checkbox
                                checked={isSeller}
                                onCheckedChange={(v) => setIsSeller(!!v)}
                              />
                              <span className="text-sm">
                                {c.seller ?? "Säljare"}
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{c.address ?? "Adress"}</Label>
                        <div className="space-y-2">
                          <Input
                            value={street}
                            onChange={(e) => setStreet(e.target.value)}
                            placeholder={c.placeholderStreet ?? "Gatuadress"}
                          />
                          <div className="grid grid-cols-3 gap-2">
                            <Input
                              value={postalCode}
                              onChange={(e) => {
                                if (POSTAL_DIGITS.test(e.target.value))
                                  setPostalCode(e.target.value);
                              }}
                              placeholder={c.placeholderPostalCode ?? "Postnr"}
                            />
                            <Input
                              value={city}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (CITY_COUNTRY_LETTERS.test(v) || v === "")
                                  setCity(v);
                              }}
                              placeholder={c.placeholderCity ?? "Ort"}
                            />
                            <Input
                              value={country}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (CITY_COUNTRY_LETTERS.test(v) || v === "")
                                  setCountry(v);
                              }}
                              placeholder={c.placeholderCountry ?? "Land"}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-muted-foreground text-xs">
                          {c.linkedVehicles ?? "Kopplade fordon"}
                        </Label>
                        <div className="flex flex-wrap gap-2">
                          {linkedVehicles.map((v) => (
                            <Badge
                              key={v.id}
                              variant="secondary"
                              className="gap-1 pr-1"
                            >
                              {v.regNr}
                              <button
                                type="button"
                                onClick={() =>
                                  setLinkedVehicles((prev) =>
                                    prev.filter((x) => x.id !== v.id),
                                  )
                                }
                                className="rounded hover:bg-muted-foreground/20 ml-0.5"
                                aria-label={c.remove ?? c.delete ?? "Ta bort"}
                              >
                                ×
                              </button>
                            </Badge>
                          ))}
                        </div>
                        <Select
                          onValueChange={(vehicleId) => {
                            const v = companyVehicles.find(
                              (x) => x.id === vehicleId,
                            );
                            if (
                              v &&
                              !linkedVehicles.some((lv) => lv.id === v.id)
                            ) {
                              setLinkedVehicles((prev) => [
                                ...prev,
                                { id: v.id, regNr: v.regNr ?? v.id },
                              ]);
                            }
                          }}
                          value=""
                        >
                          <SelectTrigger className="h-8 w-full max-w-xs">
                            <SelectValue
                              placeholder={
                                c.addLinkedVehicle ?? "Lägg till fordon"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {companyVehicles
                              .filter(
                                (v) =>
                                  !linkedVehicles.some((lv) => lv.id === v.id),
                              )
                              .map((v) => (
                                <SelectItem key={v.id} value={v.id}>
                                  {v.regNr} – {v.make} {v.model}
                                </SelectItem>
                              ))}
                            {companyVehicles.filter(
                              (v) =>
                                !linkedVehicles.some((lv) => lv.id === v.id),
                            ).length === 0 && (
                              <SelectItem value="_none" disabled>
                                {vehiclesQuery.isLoading
                                  ? (c.loadingVehicles ??
                                    c.loading ??
                                    "Laddar...")
                                  : (c.noMoreVehicles ?? "Inga fler fordon")}
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="submit"
                        disabled={
                          updateCustomer.isPending ||
                          updateCustomerVehicleConnections.isPending
                        }
                      >
                        {updateCustomer.isPending ||
                        updateCustomerVehicleConnections.isPending
                          ? (c.saving ?? "Sparar...")
                          : (c.save ?? "Spara")}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setEditing(false)}
                      >
                        {c.cancel ?? "Avbryt"}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <h4 className="text-xs font-semibold text-muted-foreground border-b pb-1 mb-2">
                          {c.contactInfo ?? "Kontaktinformation"}
                        </h4>
                        {(cust.customerType ||
                          cust.isBuyer ||
                          cust.isSeller) && (
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            {cust.customerType && (
                              <Badge
                                variant="secondary"
                                className={
                                  CUSTOMER_TYPE_BADGE_CLASS[cust.customerType]
                                }
                              >
                                {cust.customerType === "PRIVATE"
                                  ? (c.typePrivate ?? "Privatkund")
                                  : cust.customerType === "DEALER"
                                    ? (c.typeDealer ?? "Handlare")
                                    : (c.typeBusiness ?? "Företag")}
                              </Badge>
                            )}
                            {cust.isBuyer && (
                              <Badge
                                variant="secondary"
                                className="text-[10px] font-normal bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
                              >
                                {c.buyer ?? "Köpare"}
                              </Badge>
                            )}
                            {cust.isSeller && (
                              <Badge
                                variant="secondary"
                                className="text-[10px] font-normal bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                              >
                                {c.seller ?? "Säljare"}
                              </Badge>
                            )}
                          </div>
                        )}
                        <p>
                          <span className="text-muted-foreground">
                            {c.personalNumber ?? "Personnummer"}:
                          </span>{" "}
                          {cust.customerType === "PRIVATE" &&
                          cust.personalNumber
                            ? cust.personalNumber
                            : "—"}
                        </p>
                        {(cust.customerType === "BUSINESS" ||
                          cust.customerType === "DEALER") &&
                          cust.orgNumber && (
                            <p className="mt-2">
                              <span className="text-muted-foreground">
                                {c.orgNr ?? "Org.nr"}:
                              </span>{" "}
                              {cust.orgNumber}
                            </p>
                          )}
                        {(cust.customerType === "BUSINESS" ||
                          cust.customerType === "DEALER") &&
                          cust.contactPersonName && (
                            <div className="mt-2 pt-2 border-t">
                              <p className="text-xs font-semibold text-muted-foreground mb-1">
                                {c.contactPerson ?? "Kontaktperson"}
                              </p>
                              <p>{cust.contactPersonName}</p>
                            </div>
                          )}
                        <p>
                          <span className="text-muted-foreground">
                            {c.emailLabel ?? "E-post"}:
                          </span>{" "}
                          {cust.email || "—"}
                        </p>
                        <p>
                          <span className="text-muted-foreground">
                            {c.phoneLabel ?? "Telefon"}:
                          </span>{" "}
                          {cust.phone || "—"}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold text-muted-foreground border-b pb-1 mb-2">
                          {c.address ?? "Adress"}
                        </h4>
                        <p>{cust.street || "—"}</p>
                        <p>
                          {[cust.postalCode, cust.city]
                            .filter(Boolean)
                            .join(" ") || "—"}
                        </p>
                        <p>{cust.country || "—"}</p>
                        <h4 className="text-xs font-semibold text-muted-foreground border-b pb-1 mb-2 mt-3">
                          {c.identificationAndConnections ??
                            "Identifikation & kopplingar"}
                        </h4>
                        <p>
                          <span className="text-muted-foreground">
                            {c.regNr ?? "Reg.nr"}:
                          </span>{" "}
                          {(cust.vehicleConnections ?? [])
                            .map(
                              (vc: DealerPortalCustomerVehicleConnection) =>
                                vc.vehicle?.regNr ?? "",
                            )
                            .filter(Boolean)
                            .join(", ") || "—"}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={startEditing}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1" />
                        {c.edit ?? "Redigera"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => setShowDeleteConfirm(true)}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" />
                        {c.delete ?? "Radera"}
                      </Button>
                    </div>
                  </>
                )}
              </TabsContent>

              <TabsContent value="documents" className="mt-3">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <h3 className="text-sm font-semibold">
                      {c.tabDocuments ?? "Dokument"}
                    </h3>
                    <div className="flex items-center gap-2">
                      <FileUploadTrigger
                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                        disabled={uploadingDoc}
                        onFilesSelected={(files) => {
                          if (!files[0]) return;
                          const dt = new DataTransfer();
                          dt.items.add(files[0]);
                          handleUploadDocument({
                            target: { files: dt.files },
                          } as React.ChangeEvent<HTMLInputElement>);
                        }}
                      >
                        {(open) => (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={uploadingDoc}
                            onClick={open}
                            className="gap-1"
                          >
                            {uploadingDoc ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Upload className="h-3.5 w-3.5" />
                            )}
                            {c.uploadDocument ?? "Ladda upp"}
                          </Button>
                        )}
                      </FileUploadTrigger>
                    </div>
                  </div>
                  {(cust.documents?.length ?? 0) === 0 ? (
                    <p className="text-sm text-muted-foreground py-4">
                      {c.noDocuments ?? "Inga dokument uppladdade"}
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {(cust.documents ?? []).map(
                        (doc: DealerPortalCustomerDocument) => (
                          <li
                            key={doc.id}
                            className="flex items-center justify-between gap-2 p-2 rounded border bg-muted/30"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                              <span className="text-sm font-medium truncate">
                                {doc.media?.originalName ?? doc.id}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleDownloadDocument(doc)}
                                disabled={!doc.media?.id}
                                title={c.download ?? "Ladda ned"}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                onClick={() => handleDeleteDocument(doc)}
                                disabled={deletingDocId === doc.id}
                                title={c.delete ?? "Radera"}
                              >
                                {deletingDocId === doc.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </li>
                        ),
                      )}
                    </ul>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="history" className="mt-3">
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold">
                    {c.tabHistory ?? "Historik"}
                  </h3>
                  {(() => {
                    const historyEvents = (cust.historyEvents ?? []).filter(
                      (ev: DealerPortalCustomerHistoryEvent) =>
                        ev.eventType !== "COMMENT",
                    );
                    return historyEvents.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        {c.noHistory ?? "Ingen historik än"}
                      </p>
                    ) : (
                      <div className="relative space-y-2">
                        <div className="absolute left-2 top-4 bottom-0 w-px bg-border" />
                        {historyEvents.map(
                          (ev: DealerPortalCustomerHistoryEvent) => {
                            const isJourneyRelated =
                              !!ev.journeyId ||
                              [
                                "CREATED",
                                "STATUS_CHANGE",
                                "ASSIGNMENT",
                                "PRIORITY_CHANGE",
                                "JOURNEY_DELETED",
                              ].includes(ev.eventType);
                            return (
                              <div
                                key={ev.id}
                                className="relative flex gap-3 pl-4"
                              >
                                <div
                                  className={`w-3 h-3 rounded-full border-2 absolute left-0.5 top-1 ${
                                    isJourneyRelated
                                      ? "border-blue-500 bg-blue-50 dark:bg-blue-950/40"
                                      : "border-primary bg-background"
                                  }`}
                                />
                                <div
                                  className={`rounded p-2 text-sm ${
                                    isJourneyRelated
                                      ? "bg-blue-50/80 dark:bg-blue-950/30 border-l-2 border-blue-400"
                                      : "bg-muted/50"
                                  }`}
                                >
                                  <p className="font-medium">
                                    {ev.description ?? ev.eventType}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {new Date(ev.createdAt).toLocaleString(
                                      language === "sv" ? "sv-SE" : "en",
                                    )}
                                    {ev.actor?.user?.username
                                      ? ` • ${ev.actor.user.username}`
                                      : ""}
                                  </p>
                                </div>
                              </div>
                            );
                          },
                        )}
                      </div>
                    );
                  })()}
                </div>
              </TabsContent>

              <TabsContent value="notes" className="mt-3">
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      placeholder={c.addNote ?? "Lägg till anteckning"}
                      value={noteContent}
                      onChange={(e) => setNoteContent(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" &&
                        (e.preventDefault(), handleAddNote())
                      }
                    />
                    <Button
                      onClick={handleAddNote}
                      disabled={
                        !noteContent.trim() || addCustomerNote.isPending
                      }
                    >
                      {c.addNoteButton ?? "Lägg till"}
                    </Button>
                  </div>
                  {(cust.notes?.length ?? 0) === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {c.noNotes ?? "Inga anteckningar"}
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {(cust.notes ?? []).map(
                        (note: DealerPortalCustomerNote) => (
                          <li
                            key={note.id}
                            className={
                              note.journeyId
                                ? "bg-blue-50/80 dark:bg-blue-950/30 border-l-2 border-blue-400 rounded p-2 text-sm"
                                : "bg-muted/50 rounded p-2 text-sm"
                            }
                          >
                            <p>{note.content}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(note.createdAt).toLocaleString(
                                language === "sv" ? "sv-SE" : "en",
                              )}
                              {note.actor?.user?.username
                                ? ` • ${note.actor.user.username}`
                                : ""}
                            </p>
                          </li>
                        ),
                      )}
                    </ul>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}

          {!cust && !isLoading && open && (
            <p className="text-sm text-muted-foreground">
              {c.loadError ?? "Kunde inte ladda kund."}
            </p>
          )}
        </div>
      </DialogContent>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {c.deleteTitle ?? "Radera kund?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {c.deleteDesc ??
                "Är du säker på att du vill radera denna kund? Namn, e-post och telefon tas bort permanent."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{c.cancel ?? "Avbryt"}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCustomer}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {c.delete ?? "Radera"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
